import { NextPage } from 'next';
import { useContext, useEffect, useRef } from 'react';
import { apiPlansGetV2, planPageType } from 'api/plans/get';
import { ProductsContext } from '../../context-providers/products-provider';
import { YotpoReviewScript } from '../../components/pdp/yotpo-reviews-script';
import { ReviewsDisplay } from '../../components/pdp/reviews-display';
import { YotpoLlmSchema } from 'components/pdp/yotpo-llm-schema';

import { getShopifyVariantResults } from 'lib/shopify/get-shopify-variants-direct';
import { META_DUO_SKU, PRODUCT_SKUS } from 'lib/constants/product-skus';
import { getYotpoBottomline } from 'lib/utils/get-yotpo-bottomline';
import { getYotpoLlmSchema } from 'lib/utils/get-yotpo-llm-schema';
import { applyShopifyPercentDiscount } from 'lib/utils/shopify-percent-discount';
import { BUNDLE_DISCOUNT_PERCENT } from 'lib/constants/bundle-discount';
import { createGlobalStyle } from 'styled-components';
import { mqXXLargeMin, mqLargeMin } from 'styled/tools/media-queries';
import { PageTypePdp } from 'components/styles/pdp';
import { getProductProps } from 'lib/utils/get-product-static-props';
import { attachPdpModulesToProps } from 'lib/pdp-modules/get-pdp-modules';
import type { PdpModules, PdpModulesSource } from 'lib/pdp-modules/types';
import PageHead from 'components/duo/page-head';
import { InternationalCurrencyModal } from 'components/pdp/international-currency-modal';
import { CheckoutProvider } from 'context-providers/checkout-provider';
import { UserContext } from 'context-providers/user-provider';
import { buildMetaUserData, trackEvent } from 'lib/analytics';
import { getCurrentCurrency } from 'lib/utils/current-currency';
import { getLocalizedPrice } from 'lib/utils/get-localized-price';

import LazyLoad from 'components/reusable/Lazy/lazy_load';
import Buybox from 'components/pdp/buybox';
import ProductEssentials from 'components/pdp/product-essentials';
import ProductBenefits from 'components/pdp/product-benefits';
import ProductNutrients from 'components/pdp/product-nutrients';
import ProductTesting from 'components/pdp/product-testing';
import ProductBundleItems from 'components/pdp/product-bundle-items';

interface DailyEssentialsDuoPageProps {
  yotpoAverageScore: string | null;
  yotpoTotalReviews: string | null;
  dsYotpoLlmSchemaHtml?: string;
  dmYotpoLlmSchemaHtml?: string;
  modules: PdpModules;
  modulesSource: PdpModulesSource;
}

const GlobalStylePDPPage = createGlobalStyle`
  .site-content {
    background-color: var(--fill-primary);

    @media ${mqLargeMin}{
      padding: 0;
    }
    #srp0-sticky {
      z-index: 100;
      transform: translateY(0);
    }
    #add-to-cart-new {
      display: none;
    }
  }
  .optly-hero-text {
    top: 20% !important;
    @media ${mqXXLargeMin} {
      top: 25% !important;
    }
  }

  #add-to-cart-cta:focus {
    background-color: var(--fill-brand) !important;
  }

  ${PageTypePdp}
`;

const DailyEssentialsDuoPage: NextPage<DailyEssentialsDuoPageProps> = ({
  yotpoAverageScore,
  yotpoTotalReviews,
  dsYotpoLlmSchemaHtml,
  dmYotpoLlmSchemaHtml,
  siteBuy,
  productInfo,
  modules,
}) => {
  return (
    <CheckoutProvider>
      <DailyEssentialsDuo
        yotpoAverageScore={yotpoAverageScore}
        yotpoTotalReviews={yotpoTotalReviews}
        dsYotpoLlmSchemaHtml={dsYotpoLlmSchemaHtml}
        dmYotpoLlmSchemaHtml={dmYotpoLlmSchemaHtml}
        siteBuy={siteBuy}
        productInfo={productInfo}
        modules={modules}
      />
    </CheckoutProvider>
  );
};

const DailyEssentialsDuo = ({
  yotpoAverageScore,
  yotpoTotalReviews,
  dsYotpoLlmSchemaHtml,
  dmYotpoLlmSchemaHtml,
  siteBuy,
  productInfo,
  modules,
}: {
  yotpoAverageScore: string | null;
  yotpoTotalReviews: string | null;
  dsYotpoLlmSchemaHtml?: string;
  dmYotpoLlmSchemaHtml?: string;
  modules: PdpModules;
}) => {
  const { getProduct } = useContext(ProductsContext);

  const { userState } = useContext(UserContext);

  const dsProduct = getProduct(PRODUCT_SKUS.DAILY_SYNBIOTIC);
  const dmProduct = getProduct(PRODUCT_SKUS.DAILY_MULTIVITAMIN);

  // This sends one Product Viewed for the Duo so Meta gets a ViewContent for
  // the daily-essentials-duo SKU at the bundle price shown on the page. It only
  // goes to Google Tag Manager (where the Meta tag lives) so Amplitude and GA4
  // don't change.
  const viewTracked = useRef(false);
  useEffect(() => {
    if (viewTracked.current || !dsProduct?.productSku || !dmProduct?.productSku)
      return;
    viewTracked.current = true;
    const price = Number(
      (
        parseFloat(
          applyShopifyPercentDiscount(
            getLocalizedPrice(dsProduct),
            BUNDLE_DISCOUNT_PERCENT,
          ) ?? '0',
        ) +
        parseFloat(
          applyShopifyPercentDiscount(
            getLocalizedPrice(dmProduct),
            BUNDLE_DISCOUNT_PERCENT,
          ) ?? '0',
        )
      ).toFixed(2),
    );
    (async () => {
      const userData = await buildMetaUserData(userState);
      trackEvent(
        'Product Viewed',
        {
          sku: META_DUO_SKU,
          name: 'Daily Essentials Duo',
          category: 'Probiotic',
          brand: 'Seed',
          price,
          value: price,
          quantity: 1,
          currency: getCurrentCurrency(),
          content_ids: [META_DUO_SKU],
          content_type: 'product',
          contents: [
            {
              id: META_DUO_SKU,
              quantity: 1,
              item_price: price,
            },
          ],
          num_items: 1,
          ...(userData ? { userData } : {}),
        },
        'Default',
        { integrations: { All: false, 'Google Tag Manager': true } },
      );
    })();
  }, [dsProduct, dmProduct]);

  // Bundle price for the JSON-LD offer (Google rich results). Shopify
  // discounts each line separately, so sum the discounted lines to match
  // the buybox, cart, and checkout.
  const dsBundlePrice = applyShopifyPercentDiscount(
    dsProduct?.price,
    BUNDLE_DISCOUNT_PERCENT,
  );
  const dmBundlePrice = applyShopifyPercentDiscount(
    dmProduct?.price,
    BUNDLE_DISCOUNT_PERCENT,
  );
  const bundlePrice =
    dsBundlePrice && dmBundlePrice
      ? (parseFloat(dsBundlePrice) + parseFloat(dmBundlePrice)).toFixed(2)
      : undefined;

  return (
    <>
      <GlobalStylePDPPage />
      {bundlePrice && (
        <PageHead
          bundlePrice={bundlePrice}
          yotpoAverageScore={yotpoAverageScore}
          yotpoTotalReviews={yotpoTotalReviews}
        />
      )}
      <YotpoReviewScript />
      <div className='container'>
        <InternationalCurrencyModal location='Daily Synbiotic PDP' />
        {productInfo && (
          <>
            <Buybox
              siteBuy={siteBuy}
              useCartContext
              selectedSkus={
                [
                  PRODUCT_SKUS.DAILY_SYNBIOTIC,
                  PRODUCT_SKUS.DAILY_MULTIVITAMIN,
                ] as string[]
              }
              productInfo={productInfo}
              buyBox={modules.buyBox}
              bundleProducts={modules.bundleInfoModule}
              yotpoAverageScore={yotpoAverageScore}
              yotpoTotalReviews={yotpoTotalReviews}
            />
            {modules.essentialsModule && (
              <LazyLoad height='200px'>
                <ProductEssentials
                  productInfo={productInfo}
                  essentials={modules.essentialsModule}
                  bundleItems={modules.essentialsBundleItems}
                />
              </LazyLoad>
            )}
            {modules.benefitsModule && (
              <LazyLoad height='200px'>
                <ProductBenefits benefits={modules.benefitsModule} />
              </LazyLoad>
            )}
            {modules.nutrientsModule && (
              <LazyLoad height='200px'>
                <ProductNutrients
                  nutrients={modules.nutrientsModule}
                  productInfo={productInfo}
                />
              </LazyLoad>
            )}
            {modules.testingStandardsModule && (
              <LazyLoad height='200px'>
                <ProductTesting
                  testingModule={modules.testingStandardsModule}
                  productInfo={productInfo}
                />
              </LazyLoad>
            )}
            {modules.bundleInfoModule && (
              <LazyLoad height='200px'>
                <ProductBundleItems
                  bundleProducts={modules.bundleInfoModule}
                  selectedSkus={[
                    PRODUCT_SKUS.DAILY_SYNBIOTIC,
                    PRODUCT_SKUS.DAILY_MULTIVITAMIN,
                  ]}
                />
              </LazyLoad>
            )}
          </>
        )}
      </div>

      {dsProduct && (
        <ReviewsDisplay
          imageUrl='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1722005137/Channel%20Catalogs/1_1_DM-02_2x.png'
          product={dsProduct}
          url='/daily-synbiotic'
          price={dsProduct?.price}
        />
      )}
      <YotpoLlmSchema html={dsYotpoLlmSchemaHtml} />

      {dmProduct && (
        <ReviewsDisplay
          imageUrl='https://res.cloudinary.com/dljz0lko8/image/upload/f_auto,q_auto/v1722005137/Channel%20Catalogs/1_1_AM_2x.png'
          product={dmProduct}
          url='/daily-multivitamin'
          price={dmProduct?.price}
        />
      )}
      <YotpoLlmSchema html={dmYotpoLlmSchemaHtml} />
    </>
  );
};

export async function getStaticProps() {
  const products = await apiPlansGetV2(planPageType.all);
  const bottomlines = await Promise.all([
    getYotpoBottomline(PRODUCT_SKUS.DAILY_SYNBIOTIC),
    getYotpoBottomline(PRODUCT_SKUS.DAILY_MULTIVITAMIN),
  ]);
  let totalReviews = 0;
  let totalScore = 0;
  for (const {
    yotpoAverageScore: score,
    yotpoTotalReviews: count,
  } of bottomlines) {
    if (score == null || !count) continue;
    totalReviews += count;
    totalScore += score * count;
  }
  const yotpoAverageScore = totalReviews
    ? (totalScore / totalReviews).toFixed(1)
    : null;
  const yotpoTotalReviews = totalReviews || null;

  const [dsYotpoLlmSchemaHtml, dmYotpoLlmSchemaHtml] = await Promise.all([
    getYotpoLlmSchema(PRODUCT_SKUS.DAILY_SYNBIOTIC),
    getYotpoLlmSchema(PRODUCT_SKUS.DAILY_MULTIVITAMIN),
  ]);

  const props: Record<string, unknown> = {
    products,
    yotpoAverageScore,
    yotpoTotalReviews,
    dsYotpoLlmSchemaHtml,
    dmYotpoLlmSchemaHtml,
  };

  await getProductProps({
    props: props,
    productHandle: 'ds-01®-dm-02™-welcome-kit',
  });

  await attachPdpModulesToProps('duo', props);

  const { results } = await getShopifyVariantResults({
    requiredSkus: [
      PRODUCT_SKUS.DAILY_SYNBIOTIC,
      PRODUCT_SKUS.DAILY_MULTIVITAMIN,
    ],
  });
  props.shopifyProducts = results;

  return {
    props: props,
    revalidate: 60,
  };
}

export default DailyEssentialsDuoPage;
